import Accordion from '@src/components/reusable/Accordion';
import { DirectLink, PaymentMethodCode, PaymentOption, Order, Sale, PaymentCardParams } from '@src/core/buyerApiClient';
import useTranslate from '@src/i18n/useTranslate';
import React from 'react';
import { MdCreditCard } from 'react-icons/md';
import { Title, Header, Radio, Container } from './styles';
import PixIcon from '@src/icons/pix.svg';
import { getPaymentStatus, paymentErrorsSelector, paymentOngoingSelector, processPayment } from '@src/features/payment';
import { useAppDispatch, useAppSelector, useWidgetParam } from '@src/core/hooks';
import { authSelector } from '@src/features/auth';
import { ClassNamespace } from '@src/common';
import PhoneVerificationSection from './PhoneVerificationSection';
import CardForm from './CardForm';
import PixForm from './PixForm';
import BackdropLoading from '@src/components/ui2/BackdropLoading';

export type PaymentMethodFormProps = {
  cn?: ClassNamespace;
  order?: Order;
  sale?: Sale;
  directLink?: DirectLink;
  paymentOption: PaymentOption;
  onSubmit: (paymentMethodId: string, cardValues?: PaymentCardParams) => void;
  saleStatusRequester: () => void;
  contextErrors: { [key: string]: string[] };
  inputClass?: string;
  unavailableMessage?: string;
};

type PaymentMethodProps = {
  order?: Order;
  sale: Sale;
  directLink?: DirectLink;
  paymentOption: PaymentOption;
  onClick: (opt: PaymentOption) => void;
  selected?: boolean;
};

const cardMethods: PaymentMethodCode[] = [PaymentMethodCode.CreditCard, PaymentMethodCode.DebitCard];

const PaymentMethod = (props: PaymentMethodProps) => {
  const { paymentOption, onClick = () => {}, selected = false, sale, order, directLink } = props;

  const dispatch = useAppDispatch();
  const { i18n } = useTranslate();

  const { buyer } = useAppSelector(authSelector);
  const paymentOngoing = useAppSelector(paymentOngoingSelector);
  const errors = useAppSelector(paymentErrorsSelector);

  const inputClass = useWidgetParam('input-class') || 'input';

  const { code } = paymentOption.paymentMethod;
  const isCardPaymentMethod = cardMethods.includes(code);
  const isPixPaymentMethod = code === PaymentMethodCode.Pix;

  const CustomHeader = (_: boolean, title: string) => {
    return (
      <Header>
        <Radio type='radio' checked={selected} readOnly />

        {isCardPaymentMethod ? <MdCreditCard size={25} color='#4d4d4d' /> : null}

        {isPixPaymentMethod ? <PixIcon /> : null}

        <Title>
          {title}

          {paymentOption.paymentMethod.discountPercentage ? (
            <span>{` (${paymentOption.paymentMethod.discountPercentage.toString().replace('.', ',')}${i18n(
              'checkout_percentDiscount',
            )})`}</span>
          ) : null}
        </Title>
      </Header>
    );
  };

  const renderPaymentForm = (props: PaymentMethodFormProps) => {
    switch (props.paymentOption?.paymentMethod.code) {
      case PaymentMethodCode.CreditCard:
      case PaymentMethodCode.DebitCard:
        return <CardForm {...props} />;
      case PaymentMethodCode.Pix:
        return <PixForm {...props} />;
      default:
        return null;
    }
  };

  const handlePaymentSubmit = (paymentMethodId: string, cardValues?: PaymentCardParams) => {
    dispatch(
      processPayment({
        order,
        sale,
        directLink,
        paymentMethodId: paymentMethodId,
        paymentCard: cardValues,
      }),
    );
  };

  return (
    <Container $open={selected}>
      <Accordion
        open={selected}
        header={CustomHeader}
        title={paymentOption.paymentMethod.name}
        onClick={() => onClick(paymentOption)}
      >
        {paymentOngoing && <BackdropLoading />}

        {paymentOption?.needsPhoneVerification && !buyer?.phoneVerifiedAt ? (
          <PhoneVerificationSection />
        ) : (
          renderPaymentForm({
            cn: undefined, // cn
            order,
            sale,
            directLink,
            paymentOption,
            onSubmit: handlePaymentSubmit,
            saleStatusRequester: () => dispatch(getPaymentStatus({ directLink, sale, order })),
            contextErrors: errors,
            inputClass,
            unavailableMessage: i18n('checkout_unavailablePaymentMethod'),
          })
        )}
      </Accordion>
    </Container>
  );
};

export default PaymentMethod;
